import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSocketEvent } from '../hooks/useSocketEvent';
import { socket } from '../lib/socket';
import { EVENTS } from '@flip7/shared';
import type { PresenceData, PresenceBulkPayload, PresenceUpdatePayload, GameInvitePayload, GroupInvitePayload, FriendRequestPayload, FriendAcceptedPayload } from '@flip7/shared';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface GroupSummary {
  id: string;
  name: string;
  slug: string;
  _count: { members: number };
}

interface GroupDetail {
  id: string;
  name: string;
  slug: string;
  creatorId: string;
  members: { nickname: string | null; user: { id: string; name: string; activeAvatar: string | null } }[];
  activeRoom: { roomCode: string; status: string; playerCount: number } | null;
}

interface Friend {
  friendshipId: string;
  id: string;
  name: string;
  activeAvatar: string | null;
  activeTitle: string | null;
}

interface FriendRequest {
  id: string;
  user: { id: string; name: string; activeAvatar: string | null };
}

interface OutgoingRequest {
  id: string;
  friend: { id: string; name: string; activeAvatar: string | null };
}

export interface GameInvite {
  inviteId: string;
  fromUser: { id: string; name: string; avatar: string | null };
  roomCode: string;
  gameMode: string;
  playerCount: number;
  maxPlayers: number;
  type: 'game';
}

export interface GroupInvite {
  inviteId: string;
  fromUser: { id: string; name: string; avatar: string | null };
  groupSlug: string;
  groupName: string;
  type: 'group';
}

export type SocialNotification = GameInvite | GroupInvite;

interface SocialPanelProps {
  onJoinGroup: (slug: string) => void;
  onJoinRoom?: (roomCode: string) => void;
  onSpectateRoom?: (roomCode: string) => void;
  currentRoomCode?: string | null;
  /** When true, only show the friends tab (no groups). Used in drawer mid-game. */
  friendsOnly?: boolean;
}

export function SocialPanel({ onJoinGroup, onJoinRoom, onSpectateRoom, currentRoomCode, friendsOnly }: SocialPanelProps) {
  const { accessToken, user } = useAuth();
  const [tab, setTab] = useState<'groups' | 'friends'>(friendsOnly ? 'friends' : 'groups');
  const [presences, setPresences] = useState<Map<string, PresenceData>>(new Map());
  const [notifications, setNotifications] = useState<SocialNotification[]>([]);
  const [friendRequestCount, setFriendRequestCount] = useState(0);

  // Request initial presence data when panel mounts
  useEffect(() => {
    if (accessToken && socket.connected) {
      socket.emit(EVENTS.REQUEST_PRESENCE);
    }
  }, [accessToken]);

  // Listen for presence updates
  useSocketEvent<PresenceBulkPayload>(EVENTS.PRESENCE_BULK, (data) => {
    setPresences(prev => {
      const next = new Map(prev);
      for (const p of data.presences) {
        next.set(p.userId, p);
      }
      return next;
    });
  });

  useSocketEvent<PresenceUpdatePayload>(EVENTS.PRESENCE_UPDATE, (data) => {
    setPresences(prev => {
      const next = new Map(prev);
      next.set(data.userId, data);
      return next;
    });
  });

  // Listen for game invites
  useSocketEvent<GameInvitePayload>(EVENTS.GAME_INVITE, (data) => {
    setNotifications(prev => [...prev, { ...data, type: 'game' }]);
  });

  // Listen for group invites
  useSocketEvent<GroupInvitePayload>(EVENTS.GROUP_INVITE, (data) => {
    setNotifications(prev => [...prev, { ...data, type: 'group' }]);
  });

  // Listen for expired invites
  useSocketEvent<{ inviteId: string }>(EVENTS.INVITE_EXPIRED, (data) => {
    setNotifications(prev => prev.filter(n => n.inviteId !== data.inviteId));
  });

  const respondToInvite = useCallback((inviteId: string, accepted: boolean) => {
    socket.emit(EVENTS.RESPOND_INVITE, { inviteId, accepted });
    const invite = notifications.find(n => n.inviteId === inviteId);
    setNotifications(prev => prev.filter(n => n.inviteId !== inviteId));
    if (accepted && invite) {
      if (invite.type === 'game' && onJoinRoom) {
        onJoinRoom(invite.roomCode);
      } else if (invite.type === 'group') {
        onJoinGroup(invite.groupSlug);
      }
    }
  }, [notifications, onJoinRoom, onJoinGroup]);

  if (!accessToken) {
    return (
      <div className="social-empty">
        <span className="social-empty__icon">&#x1F46B;</span>
        <p className="social-empty__text">Sign in to use social features.</p>
      </div>
    );
  }

  const totalBadge = notifications.length + friendRequestCount;

  return (
    <div className="social-panel">
      {/* Pending invites */}
      {notifications.length > 0 && (
        <div className="social-invites">
          <p className="label" style={{ marginBottom: '8px' }}>
            Invites ({notifications.length})
          </p>
          <div className="flex-col gap-sm">
            {notifications.map(n => (
              <div key={n.inviteId} className="player-row player-row--winner" style={{ padding: '10px 12px' }}>
                <div className="social-invite__info">
                  <span className="social-invite__name">{n.fromUser.name}</span>
                  <p className="social-invite__desc">
                    {n.type === 'game'
                      ? `Invited you to play ${n.gameMode} (${n.playerCount}/${n.maxPlayers})`
                      : `Invited you to join ${n.groupName}`}
                  </p>
                </div>
                <div className="social-invite__actions">
                  <button className="btn btn-primary btn-small" onClick={() => respondToInvite(n.inviteId, true)}>
                    Accept
                  </button>
                  <button className="btn btn-secondary btn-small" onClick={() => respondToInvite(n.inviteId, false)}>
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sub-tabs (hidden when friendsOnly) */}
      {!friendsOnly && (
        <div className="social-tabs">
          {(['groups', 'friends'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`social-tab${tab === t ? ' social-tab--active' : ''}`}
            >
              {t === 'groups' ? 'Groups' : 'Friends'}
              {t === 'friends' && totalBadge > 0 && (
                <span className="social-tab__badge">{totalBadge}</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="social-content">
        {tab === 'groups' ? (
          <GroupsTab accessToken={accessToken} userId={user?.id} onJoinGroup={onJoinGroup} currentRoomCode={currentRoomCode} />
        ) : (
          <FriendsTab
            accessToken={accessToken}
            presences={presences}
            onJoinRoom={onJoinRoom}
            onSpectateRoom={onSpectateRoom}
            currentRoomCode={currentRoomCode}
            onFriendRequestCountChange={setFriendRequestCount}
          />
        )}
      </div>
    </div>
  );
}

function GroupsTab({ accessToken, userId, onJoinGroup, currentRoomCode }: { accessToken: string; userId?: string; onJoinGroup: (slug: string) => void; currentRoomCode?: string | null }) {
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [error, setError] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<GroupDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [groupSubTab, setGroupSubTab] = useState<'members' | 'leaderboard' | 'history'>('members');
  const [editingNickname, setEditingNickname] = useState(false);
  const [nicknameInput, setNicknameInput] = useState('');

  const fetchGroups = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/groups`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (res.ok) setGroups(await res.json());
    setLoading(false);
  }, [accessToken]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const handleCreate = async () => {
    setError('');
    if (!name.trim() || !slug.trim()) { setError('Name and slug required'); return; }
    const res = await fetch(`${API_BASE}/api/groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ name: name.trim(), slug: slug.trim().toLowerCase() }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Failed' }));
      setError(err.message || 'Failed to create group');
      return;
    }
    setName('');
    setSlug('');
    setCreating(false);
    fetchGroups();
  };

  const openGroup = async (groupSlug: string) => {
    setLoadingDetail(true);
    const res = await fetch(`${API_BASE}/api/groups/${groupSlug}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (res.ok) setSelectedGroup(await res.json());
    setLoadingDetail(false);
  };

  const leaveGroup = async (groupSlug: string) => {
    await fetch(`${API_BASE}/api/groups/${groupSlug}/leave`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    setSelectedGroup(null);
    fetchGroups();
  };

  const deleteGroup = async (groupSlug: string) => {
    if (!window.confirm('Delete this group?')) return;
    await fetch(`${API_BASE}/api/groups/${groupSlug}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    setSelectedGroup(null);
    fetchGroups();
  };

  if (loading) {
    return (
      <div className="social-loading">
        <p className="social-loading__text">Loading...</p>
      </div>
    );
  }

  // Group detail view
  if (selectedGroup) {
    const isCreator = selectedGroup.creatorId === userId;
    return (
      <div className="flex-col gap-md">
        <button onClick={() => setSelectedGroup(null)} className="social-back-link">
          &larr; All groups
        </button>

        <div className="group-header">
          <h3 className="group-header__name">{selectedGroup.name}</h3>
          <p className="group-header__slug">/{selectedGroup.slug}</p>
          <div className="gold-divider" style={{ margin: '8px auto 0' }} />
        </div>

        {/* Active room / Start game */}
        {selectedGroup.activeRoom ? (
          <button className="btn btn-success" onClick={() => onJoinGroup(selectedGroup.slug)}>
            Join Active Game ({selectedGroup.activeRoom.playerCount} playing)
          </button>
        ) : (
          <button className="btn btn-primary" onClick={() => onJoinGroup(selectedGroup.slug)}>
            Start Game in Group
          </button>
        )}

        {/* Share link */}
        <div className="ornate-border" style={{ padding: '10px 14px' }}>
          <p className="label" style={{ margin: '0 0 6px', fontSize: '0.6rem' }}>Share Link</p>
          <div className="group-share__input-row">
            <input
              readOnly
              value={`${window.location.origin}/group/${selectedGroup.slug}`}
              onFocus={e => e.target.select()}
            />
            <button
              className="btn btn-secondary btn-small"
              onClick={() => navigator.clipboard.writeText(`${window.location.origin}/group/${selectedGroup.slug}`)}
            >
              Copy
            </button>
          </div>
        </div>

        {/* Group nickname */}
        {userId && (() => {
          const myMember = selectedGroup.members.find(m => m.user.id === userId);
          const currentNickname = myMember?.nickname;

          const saveNickname = async (value: string | null) => {
            await fetch(`${API_BASE}/api/groups/${selectedGroup.slug}/nickname`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
              body: JSON.stringify({ nickname: value }),
            });
            setEditingNickname(false);
            // Refresh group detail
            const res = await fetch(`${API_BASE}/api/groups/${selectedGroup.slug}`);
            if (res.ok) setSelectedGroup(await res.json());
          };

          return (
            <div className="ornate-border" style={{ padding: '10px 14px' }}>
              <p className="label" style={{ margin: '0 0 6px', fontSize: '0.6rem' }}>Group Nickname</p>
              {editingNickname ? (
                <div className="group-share__input-row">
                  <input
                    value={nicknameInput}
                    onChange={e => setNicknameInput(e.target.value)}
                    placeholder="Enter nickname..."
                    maxLength={20}
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter' && nicknameInput.trim()) saveNickname(nicknameInput.trim());
                      if (e.key === 'Escape') setEditingNickname(false);
                    }}
                  />
                  <button className="btn btn-primary btn-small" onClick={() => nicknameInput.trim() && saveNickname(nicknameInput.trim())}>Save</button>
                  <button className="btn btn-secondary btn-small" onClick={() => setEditingNickname(false)}>Cancel</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {currentNickname || <span style={{ color: 'var(--text-muted)' }}>Using: {myMember?.user.name}</span>}
                  </span>
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={() => { setNicknameInput(currentNickname || ''); setEditingNickname(true); }}
                    style={{ marginLeft: 'auto' }}
                  >
                    {currentNickname ? 'Edit' : 'Set'}
                  </button>
                  {currentNickname && (
                    <button className="btn btn-secondary btn-small" onClick={() => saveNickname(null)}>Clear</button>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Sub-tabs */}
        <div className="group-sub-tabs">
          {(['members', 'leaderboard', 'history'] as const).map(t => (
            <button
              key={t}
              onClick={() => setGroupSubTab(t)}
              className={`group-sub-tab${groupSubTab === t ? ' group-sub-tab--active' : ''}`}
            >
              {t === 'members' ? `Members (${selectedGroup.members.length})` : t === 'leaderboard' ? 'Rankings' : 'History'}
            </button>
          ))}
        </div>

        {groupSubTab === 'members' && (
          <GroupMembers group={selectedGroup} accessToken={accessToken} currentRoomCode={currentRoomCode} userId={userId ?? ''} />
        )}

        {groupSubTab === 'leaderboard' && (
          <GroupLeaderboard slug={selectedGroup.slug} accessToken={accessToken} userId={userId} />
        )}

        {groupSubTab === 'history' && (
          <GroupHistory slug={selectedGroup.slug} accessToken={accessToken} />
        )}

        {/* Actions */}
        <div className="group-actions">
          {!isCreator && (
            <button className="btn btn-secondary btn-small" onClick={() => leaveGroup(selectedGroup.slug)} style={{ flex: 1 }}>
              Leave Group
            </button>
          )}
          {isCreator && (
            <button className="btn btn-danger btn-small" onClick={() => deleteGroup(selectedGroup.slug)} style={{ flex: 1 }}>
              Delete Group
            </button>
          )}
        </div>
      </div>
    );
  }

  // Groups list view
  return (
    <div className="flex-col gap-md">
      {/* Create toggle */}
      {!creating ? (
        <button onClick={() => setCreating(true)} className="group-create-btn">
          + Create Group
        </button>
      ) : (
        <div className="ornate-border flex-col gap-sm">
          <p className="label">New Group</p>
          <input
            value={name}
            onChange={e => {
              setName(e.target.value);
              setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
            }}
            placeholder="Group name"
          />
          <div className="group-share__input-row">
            <span className="group-header__slug" style={{ whiteSpace: 'nowrap' }}>/group/</span>
            <input
              value={slug}
              onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="slug"
              style={{ flex: 1 }}
            />
          </div>
          {error && <p className="error-text">{error}</p>}
          <div className="flex-row gap-sm">
            <button className="btn btn-secondary btn-small" onClick={() => { setCreating(false); setError(''); }} style={{ flex: 1 }}>
              Cancel
            </button>
            <button className="btn btn-primary btn-small" onClick={handleCreate} style={{ flex: 1 }}>
              Create
            </button>
          </div>
        </div>
      )}

      {/* Group list */}
      {groups.length === 0 ? (
        <div className="social-empty">
          <span className="social-empty__icon">&#x1F3E0;</span>
          <p className="social-empty__text">No groups yet. Create one or join via a link!</p>
        </div>
      ) : (
        groups.map(g => (
          <button
            key={g.id}
            onClick={() => openGroup(g.slug)}
            disabled={loadingDetail}
            className="ornate-border group-item"
          >
            <div>
              <span className="group-item__name">{g.name}</span>
              <p className="group-item__slug">/{g.slug}</p>
            </div>
            <span className="group-item__count">{g._count.members}</span>
          </button>
        ))
      )}
    </div>
  );
}

function GroupMembers({ group, accessToken, currentRoomCode, userId }: { group: GroupDetail; accessToken: string; currentRoomCode?: string | null; userId: string }) {
  const [inviting, setInviting] = useState(false);
  const [friends, setFriends] = useState<{ id: string; name: string; activeAvatar: string | null }[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sent, setSent] = useState(false);
  const [invitedMembers, setInvitedMembers] = useState<Set<string>>(new Set());

  const loadFriends = useCallback(async () => {
    const currentMemberIds = new Set(group.members.map(m => m.user.id));
    const res = await fetch(`${API_BASE}/api/friends`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (res.ok) {
      const data = await res.json();
      setFriends(data.filter((f: { id: string }) => !currentMemberIds.has(f.id)));
    }
  }, [accessToken, group.members]);

  const sendGroupInvites = () => {
    if (selected.size === 0) return;
    socket.emit(EVENTS.SEND_GROUP_INVITE, {
      targetUserIds: Array.from(selected),
      groupSlug: group.slug,
    });
    setSent(true);
    setTimeout(() => { setSent(false); setInviting(false); setSelected(new Set()); }, 1500);
  };

  return (
    <div className="flex-col gap-xs">
      {group.members.map(m => (
        <div key={m.user.id} className="player-row member-row">
          <div className="member-row__left">
            <span className="member-row__avatar">
              {m.user.activeAvatar || m.user.name.charAt(0).toUpperCase()}
            </span>
            <span className="member-row__name">
              {m.nickname || m.user.name}
              {m.nickname && <span style={{ fontSize: '0.55rem', color: 'var(--text-muted)', marginLeft: 4 }}>({m.user.name})</span>}
            </span>
          </div>
          {currentRoomCode && m.user.id !== userId && (
            <button
              onClick={() => {
                socket.emit(EVENTS.SEND_GAME_INVITE, {
                  targetUserIds: [m.user.id],
                  roomCode: currentRoomCode,
                });
                setInvitedMembers(prev => new Set(prev).add(m.user.id));
              }}
              disabled={invitedMembers.has(m.user.id)}
              className={`member-invite-btn${invitedMembers.has(m.user.id) ? ' member-invite-btn--sent' : ''}`}
            >
              {invitedMembers.has(m.user.id) ? 'Invited' : 'Invite'}
            </button>
          )}
        </div>
      ))}

      {!inviting ? (
        <button onClick={() => { setInviting(true); loadFriends(); }} className="invite-friend-btn">
          + Invite Friends to Group
        </button>
      ) : sent ? (
        <p className="invite-success">Invites sent!</p>
      ) : (
        <div className="flex-col gap-xs" style={{ marginTop: '4px' }}>
          {friends.length === 0 ? (
            <p className="social-muted social-muted--padded">
              No friends to invite (all already members).
            </p>
          ) : (
            <>
              {friends.map(f => (
                <button
                  key={f.id}
                  onClick={() => setSelected(prev => {
                    const next = new Set(prev);
                    if (next.has(f.id)) next.delete(f.id); else next.add(f.id);
                    return next;
                  })}
                  className={`player-row friend-select${selected.has(f.id) ? ' friend-select--selected' : ''}`}
                >
                  <span className={`friend-select__check${selected.has(f.id) ? ' friend-select__check--active' : ''}`}>
                    {selected.has(f.id) ? '\u2713' : ''}
                  </span>
                  <span className="friend-select__avatar">
                    {f.activeAvatar || f.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="friend-select__name">{f.name}</span>
                </button>
              ))}
              <div className="flex-row gap-sm">
                <button className="btn btn-secondary btn-small" onClick={() => { setInviting(false); setSelected(new Set()); }} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button className="btn btn-primary btn-small" onClick={sendGroupInvites} disabled={selected.size === 0} style={{ flex: 1 }}>
                  Send ({selected.size})
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface LeaderboardEntry {
  id: string;
  rating: number;
  wins: number;
  losses: number;
  gamesPlayed: number;
  totalScore: number;
  bestScore: number;
  flip7Count: number;
  user: { id: string; name: string; activeAvatar: string | null };
}

function GroupLeaderboard({ slug, accessToken, userId }: { slug: string; accessToken: string; userId?: string }) {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/groups/${slug}/leaderboard`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(res => res.ok ? res.json() : [])
      .then(data => { setEntries(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, accessToken]);

  if (loading) return <p className="social-muted">Loading...</p>;
  if (entries.length === 0) return <p className="social-muted social-muted--padded">No games played yet. Start a game to see rankings!</p>;

  return (
    <div className="flex-col gap-xs">
      {/* Header */}
      <div className="leaderboard-header">
        <span className="leaderboard-col--rank">#</span>
        <span className="leaderboard-col--player">Player</span>
        <span className="leaderboard-col--elo">ELO</span>
        <span className="leaderboard-col--wl">W/L</span>
        <span className="leaderboard-col--avg">Avg</span>
      </div>
      {entries.map((e, i) => {
        const isMe = e.user.id === userId;
        const winRate = e.gamesPlayed > 0 ? Math.round((e.wins / e.gamesPlayed) * 100) : 0;
        const avgScore = e.gamesPlayed > 0 ? Math.round(e.totalScore / e.gamesPlayed) : 0;
        return (
          <div key={e.id} className={`player-row leaderboard-row${isMe ? ' player-row--leader' : ''}`}>
            <span className={`leaderboard-row__rank${i < 3 ? ' leaderboard-row__rank--top' : ''}`}>
              {i + 1}
            </span>
            <div className="leaderboard-row__player">
              <span className="leaderboard-row__avatar">
                {e.user.activeAvatar || e.user.name.charAt(0).toUpperCase()}
              </span>
              <div style={{ minWidth: 0 }}>
                <span className="leaderboard-row__name">{e.user.name}</span>
                <p className="leaderboard-row__sub">
                  {winRate}% WR &middot; Best: {e.bestScore} &middot; F7: {e.flip7Count}
                </p>
              </div>
            </div>
            <span className="leaderboard-row__elo">{e.rating}</span>
            <span className="leaderboard-row__stat">{e.wins}/{e.losses}</span>
            <span className="leaderboard-row__stat leaderboard-row__stat--avg">{avgScore}</span>
          </div>
        );
      })}
    </div>
  );
}

interface GameHistoryEntry {
  roomCode: string;
  gameMode: string;
  createdAt: string;
  players: { id: string; name: string; avatar: string | null; score: number; won: boolean; flip7Count: number }[];
}

function GroupHistory({ slug, accessToken }: { slug: string; accessToken: string }) {
  const [games, setGames] = useState<GameHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchHistory = useCallback((p: number) => {
    setLoading(true);
    fetch(`${API_BASE}/api/groups/${slug}/history?page=${p}&limit=10`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(res => res.ok ? res.json() : { games: [], total: 0 })
      .then(data => {
        setGames(data.games);
        setTotal(data.total);
        setPage(p);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug, accessToken]);

  useEffect(() => { fetchHistory(1); }, [fetchHistory]);

  if (loading && games.length === 0) return <p className="social-muted">Loading...</p>;
  if (games.length === 0) return <p className="social-muted social-muted--padded">No game history yet.</p>;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="flex-col gap-sm">
      {games.map(game => {
        const winner = game.players.find(p => p.won);
        return (
          <div key={game.roomCode} className="ornate-border" style={{ padding: '10px 12px' }}>
            <div className="history-game__header">
              <span className="history-game__date">
                {formatDate(game.createdAt)} &middot; {game.gameMode}
              </span>
              {winner && (
                <span className="history-game__winner">{winner.name} won</span>
              )}
            </div>
            <div className="history-game__players">
              {game.players.map(p => (
                <span key={p.id} className={`history-player${p.won ? ' history-player--winner' : ''}`}>
                  {p.name}: {p.score}
                </span>
              ))}
            </div>
          </div>
        );
      })}
      {total > page * 10 && (
        <button className="btn btn-secondary btn-small" onClick={() => fetchHistory(page + 1)} disabled={loading}>
          {loading ? 'Loading...' : 'Load More'}
        </button>
      )}
    </div>
  );
}

function PresenceDot({ presence }: { presence?: PresenceData }) {
  const online = presence?.online ?? false;
  return (
    <span className={`presence-dot${online ? ' presence-dot--online' : ''}`} />
  );
}

function FriendsTab({
  accessToken,
  presences,
  onJoinRoom,
  onSpectateRoom,
  currentRoomCode,
  onFriendRequestCountChange,
}: {
  accessToken: string;
  presences: Map<string, PresenceData>;
  onJoinRoom?: (roomCode: string) => void;
  onSpectateRoom?: (roomCode: string) => void;
  currentRoomCode?: string | null;
  onFriendRequestCountChange: (count: number) => void;
}) {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [outgoing, setOutgoing] = useState<OutgoingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [addName, setAddName] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const fetchAll = useCallback(async () => {
    const [friendsRes, requestsRes, outgoingRes] = await Promise.all([
      fetch(`${API_BASE}/api/friends`, { headers: { Authorization: `Bearer ${accessToken}` } }),
      fetch(`${API_BASE}/api/friends/requests`, { headers: { Authorization: `Bearer ${accessToken}` } }),
      fetch(`${API_BASE}/api/friends/requests/outgoing`, { headers: { Authorization: `Bearer ${accessToken}` } }),
    ]);
    if (friendsRes.ok) setFriends(await friendsRes.json());
    if (requestsRes.ok) {
      const reqs = await requestsRes.json();
      setRequests(reqs);
      onFriendRequestCountChange(reqs.length);
    }
    if (outgoingRes.ok) setOutgoing(await outgoingRes.json());
    setLoading(false);
  }, [accessToken, onFriendRequestCountChange]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Listen for real-time friend notifications
  useSocketEvent<FriendRequestPayload>(EVENTS.FRIEND_REQUEST, () => {
    fetchAll();
  });

  useSocketEvent<FriendAcceptedPayload>(EVENTS.FRIEND_ACCEPTED, () => {
    fetchAll();
  });

  const sendRequest = async () => {
    if (!addName.trim()) return;
    setSending(true);
    setError('');
    setSuccessMsg('');
    const res = await fetch(`${API_BASE}/api/friends/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ name: addName.trim() }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Failed' }));
      setError(err.message || 'Failed to send request');
    } else {
      setAddName('');
      setError('');
      setSuccessMsg('Friend request sent!');
      setTimeout(() => setSuccessMsg(''), 3000);
      fetchAll();
    }
    setSending(false);
  };

  const acceptRequest = async (id: string) => {
    await fetch(`${API_BASE}/api/friends/accept/${id}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    fetchAll();
  };

  const removeFriend = async (friendshipId: string) => {
    await fetch(`${API_BASE}/api/friends/${friendshipId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    fetchAll();
  };

  // Sort friends: online first, then by name
  const sortedFriends = [...friends].sort((a, b) => {
    const aOnline = presences.get(a.id)?.online ? 1 : 0;
    const bOnline = presences.get(b.id)?.online ? 1 : 0;
    if (aOnline !== bOnline) return bOnline - aOnline;
    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return (
      <div className="social-loading">
        <p className="social-loading__text">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex-col gap-lg">
      {/* Add friend */}
      <div className="friend-add-row">
        <input
          value={addName}
          onChange={e => setAddName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendRequest()}
          placeholder="Add friend by name"
        />
        <button
          className="btn btn-primary btn-small"
          onClick={sendRequest}
          disabled={sending || !addName.trim()}
        >
          {sending ? '...' : 'Add'}
        </button>
      </div>
      {error && <p className="error-text">{error}</p>}
      {successMsg && <p className="social-success">{successMsg}</p>}

      {/* Incoming requests */}
      {requests.length > 0 && (
        <div>
          <p className="label" style={{ marginBottom: '8px' }}>
            Requests ({requests.length})
          </p>
          <div className="flex-col gap-sm">
            {requests.map(r => (
              <div key={r.id} className="player-row player-row--winner" style={{ padding: '8px 12px' }}>
                <div className="friend-row__left">
                  <span className="friend-row__avatar">
                    {r.user.activeAvatar || r.user.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="friend-row__name">{r.user.name}</span>
                </div>
                <div className="social-invite__actions">
                  <button className="btn btn-primary btn-small" onClick={() => acceptRequest(r.id)}>
                    Accept
                  </button>
                  <button className="btn btn-secondary btn-small" onClick={() => removeFriend(r.id)}>
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Outgoing requests */}
      {outgoing.length > 0 && (
        <div>
          <p className="label" style={{ marginBottom: '8px' }}>
            Sent ({outgoing.length})
          </p>
          <div className="flex-col gap-sm">
            {outgoing.map(r => (
              <div key={r.id} className="player-row friend-row" style={{ padding: '8px 12px' }}>
                <div className="friend-row__left">
                  <span className="friend-row__avatar">
                    {r.friend.activeAvatar || r.friend.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="friend-row__name">{r.friend.name}</span>
                </div>
                <span className="friend-row__pending">Pending</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Friends list */}
      <div>
        <p className="label" style={{ marginBottom: '8px' }}>
          Friends ({friends.length})
        </p>
        {friends.length === 0 ? (
          <div className="social-empty">
            <span className="social-empty__icon">&#x1F465;</span>
            <p className="social-empty__text">No friends yet. Add someone by their username!</p>
          </div>
        ) : (
          <div className="flex-col gap-xs">
            {sortedFriends.map(f => {
              const presence = presences.get(f.id);
              const isInGame = presence?.online && presence?.inGame;
              const canJoin = isInGame && presence?.roomCode && presence.roomCode !== currentRoomCode;

              return (
                <div key={f.friendshipId} className="player-row friend-row">
                  <div className="friend-row__left">
                    <PresenceDot presence={presence} />
                    <span className="friend-row__avatar">
                      {f.activeAvatar || f.name.charAt(0).toUpperCase()}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <span className="friend-row__name">{f.name}</span>
                      {f.activeTitle && (
                        <p className="friend-row__title">{f.activeTitle}</p>
                      )}
                      {isInGame && (
                        <p className="friend-row__status">
                          {presence.gameMode} &middot; {presence.playerCount}/{presence.maxPlayers}
                          {presence.isLobby ? ' (lobby)' : ' (playing)'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="friend-row__actions">
                    {canJoin && (presence.isLobby ? onJoinRoom : onSpectateRoom) && (
                      <button
                        className="btn btn-primary btn-small"
                        onClick={() => {
                          if (presence.isLobby) {
                            onJoinRoom?.(presence.roomCode!);
                          } else {
                            onSpectateRoom?.(presence.roomCode!);
                          }
                        }}
                      >
                        {presence.isLobby ? 'Join' : 'Watch'}
                      </button>
                    )}
                    {currentRoomCode && !canJoin && (
                      <button
                        className="btn btn-secondary btn-small"
                        onClick={() => {
                          socket.emit(EVENTS.SEND_GAME_INVITE, {
                            targetUserIds: [f.id],
                            roomCode: currentRoomCode,
                          });
                        }}
                      >
                        Invite
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (window.confirm(`Remove ${f.name} as friend?`)) removeFriend(f.friendshipId);
                      }}
                      className="friend-row__remove"
                      aria-label={`Remove ${f.name}`}
                    >
                      &times;
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
